<template>
  <div class="personal-details">
    <div class="row">
      <div class="col-xs-12">
        <div class="row center-xs">
          <div class="col-xs-12 cl-mine-shaft">
            <p class="h4 m0 lh35">
              {{ $t('Personal Details') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <form novalidate autocomplete="on" id="personalDetails">
      <div class="row pt20">
        <div class="col-xs-12">
          <div class="row">
            <base-input
              class="col-xs-12 col-md-6"
              type="text"
              :autofocus="true"
              id="first-name"
              name="first-name"
              :placeholder="$t('First name *')"
              v-model.trim="personalDetails.firstName"
              @blur="$v.personalDetails.firstName.$touch()"
              @change="sendDataToCheckout"
              autocomplete="personalDetails given-name"
              :validations="[
                {
                  condition: $v.personalDetails.firstName.$error && !$v.personalDetails.firstName.required,
                  text: $t('Field is required')
                },
                {
                  condition: !$v.personalDetails.firstName.minLength,
                  text: $t('Name must have at least 2 letters.')
                }
              ]"
            />

            <base-input
              class="col-xs-12 col-md-6"
              type="text"
              id="last-name"
              name="last-name"
              :placeholder="$t('Last name *')"
              v-model.trim="personalDetails.lastName"
              @blur="$v.personalDetails.lastName.$touch()"
              autocomplete="personalDetails family-name"
              :validations="[{
                condition: $v.personalDetails.lastName.$error && !$v.personalDetails.lastName.required,
                text: $t('Field is required')
              }]"
              @change="sendDataToCheckout"
            />
            <base-telephone-input
              class="col-xs-12"
              :required="true"
              :validations="phoneValidations"
              :value="personalDetails.phoneNumber"
              @update="updatePhoneNumber"
              @input="$v.personalDetails.phoneNumber.$touch()"
            />
            <template v-if="isLoggedIn && !currentUser.phone_verified">
              <div class="verified-phone-block">
                <div class="verified-phone">
                  <span class="material-icons warning-icon">error_outline</span>
                  <span>{{ $t('Your phone number is not verified') }}.</span>
                </div>
                <span class="pointer cl-korzyna-green bold link ml10" @click="openVerifyModal">{{ $t('Verify') }}</span>
              </div>
            </template>

            <base-input
              class="col-xs-12"
              id="email-address"
              type="email"
              name="email-address"
              :placeholder="$t('Email address *')"
              v-model="personalDetails.emailAddress"
              @blur="$v.personalDetails.emailAddress.$touch()"
              autocomplete="personalDetails email"
              @change="sendDataToCheckout"
              :validations="[
                {
                  condition: $v.personalDetails.emailAddress.$error && !$v.personalDetails.emailAddress.required,
                  text: $t('Field is required')
                },
                {
                  condition: !$v.personalDetails.emailAddress.email && $v.personalDetails.emailAddress.$error,
                  text: $t('Please provide valid e-mail address.')
                }
              ]"
            />

            <template v-if="createAccount && !currentUser">
              <base-input
                class="col-xs-12  mt10"
                type="password"
                id="password"
                name="password"
                ref="password"
                :placeholder="$t('Password *')"
                v-model="password"
                @blur="$v.password.$touch()"
                autocomplete="personalDetails new-password"
                :validations="[{
                  condition: $v.password.$error && !$v.password.required,
                  text: $t('Field is required.')
                }]"
              />

              <base-input
                class="col-xs-12 mb10"
                type="password"
                id="password-confirm"
                name="password-confirm"
                :placeholder="$t('Repeat password *')"
                v-model="rPassword"
                autocomplete="personalDetails new-password"
                :validations="[
                  {
                    condition: $v.rPassword.$error && !$v.rPassword.required,
                    text: $t('Field is required.')
                  },
                  {
                    condition:!$v.rPassword.sameAsPassword,
                    text: $t('Passwords must be identical.')
                  }
                ]"
              />
            </template>
          </div>
        </div>
      </div>
    </form>
    <div class="row" v-if="!currentUser">
      <div class="col-xs-12">
        <div class="row mb30">
          <div
            class="col-xs-12 col-md-12 center-xs"
          >
            <p class="h5 my0 cl-accent">
              {{ $t('You may want to') }}
              <span
                class="link pointer cl-korzyna-green bold"
                @click.prevent="gotoAccount"
              >
                {{ $t('login to your account') }}
              </span>
            </p>
            <social-login :index="1"/>
          </div>
        </div>
      </div>
    </div>
    <verify-phone-number-modal v-if="isLoggedIn && showModal" @closeVerifyPhoneModal="closeVerifyModal" :phone="personalDetails.phoneNumber" />
  </div>
</template>

<script async>
import { required, minLength, email, sameAs } from 'vuelidate/lib/validators'
import { PersonalDetails } from '@vue-storefront/core/modules/checkout/components/PersonalDetails'
import { mapGetters } from 'vuex'

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const BaseTelephoneInput = () => import(/* webpackChunkName: "vsf-base-telephone-input" */ 'theme/components/core/blocks/Form/BaseTelephoneInput');
const SocialLogin = () => import(/* webpackChunkName: "vsf-social-login" */ '../Auth/SocialLogin');
const VerifyPhoneNumberModal = () => import(/* webpackChunkName: "vsf-verify-phone-number-modal" */ '../MyAccount/VerifyPhoneNumberModal');

export default {
  components: {
    BaseInput,
    SocialLogin,
    VerifyPhoneNumberModal,
    BaseTelephoneInput
  },
  mixins: [PersonalDetails],
  computed: {
    ...mapGetters({
      addresses: 'address/getAddressList',
      isLoggedIn: 'user/isLoggedIn'
    }),
    phoneValidations () {
      return [
        {
          condition: this.$v.personalDetails.phoneNumber.$error && !this.$v.personalDetails.phoneNumber.required,
          text: this.$t('Field is required')
        },
        {
          condition: this.$v.personalDetails.phoneNumber.$error && this.$v.personalDetails.phoneNumber.required && !this.$v.personalDetails.phoneNumber.isValid,
          text: this.$t('Incorrect type of number')
        }
      ]
    }
  },
  mounted () {
    if (this.isLoggedIn && this.currentUser) {
      this.showModal = !this.currentUser.phone_verified
    }
  },
  validations () {
    if (!this.createAccount) {
      return {
        personalDetails: {
          firstName: {
            required,
            minLength: minLength(2)
          },
          lastName: {
            required
          },
          phoneNumber: {
            required: () => required && 'phoneNumber' in this.phoneMeta,
            isValid: () => this.phoneMeta.isValid
          },
          emailAddress: {
            required,
            email
          }
        }
      }
    }
    return {
      personalDetails: {
        firstName: {
          required,
          minLength: minLength(2)
        },
        lastName: {
          required
        },
        phoneNumber: {
          required: () => required && 'phoneNumber' in this.phoneMeta,
          isValid: () => this.phoneMeta.isValid
        },
        emailAddress: {
          required,
          email
        }
      },
      password: {
        required
      },
      rPassword: {
        required,
        sameAsPassword: sameAs('password')
      }
    }
  },
  methods: {
    updatePhoneNumber (value) {
      this.phoneMeta = value;
      if ('formattedNumber' in value) {
        this.personalDetails.phoneNumber = value.formattedNumber
        if (!this.isLoggedIn || !this.currentUser) {
          return;
        }

        // Compare digits only: the phone-input library reformats the number
        // (spacing/punctuation) on every parse, including on mount when it
        // parses the already-verified stored number, so a raw string
        // comparison here would falsely mark it unverified on every load.
        if ((this.currentUser.telephone || '').replace(/\D/g, '') !== value.formattedNumber.replace(/\D/g, '')) {
          this.currentUser.phone_verified = 0
        }

        let defaultShipping = this.addresses.find(address => !!address.default_shipping)
        if (defaultShipping) {
          defaultShipping.phone_number = value.formattedNumber
          this.$bus.$emit('checkout-after-shippingDetails', defaultShipping);
        }
      }
    },
    openVerifyModal () {
      this.$bus.$emit('request-verify-phone-number')
      this.$bus.$emit('modal-show', 'verify-phone-number')
    },
    closeVerifyModal (isVerify) {
      this.showModal = isVerify
      if (this.currentUser) {
        this.currentUser.phone_verified = isVerify
      }
    }
  },
  data () {
    return {
      showModal: true,
      phoneMeta: {
        isValid: false
      },
      isNotValidPhone: false
    }
  }
}
</script>

<style lang="scss" scoped>
.warning-icon {
  width: 26px;
  height: 26px;
  color: #ff0000;
  margin-right: 5px;
}
.link {
  text-decoration: underline;
}

.login-prompt {
  @media (min-width: 1200px) {
    margin-top: 30px;
  }
}
.verified-phone {
  display: flex;
  align-items: center;
}
.verified-phone-block {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  @media (max-width: 990px) and (min-width: 769px){
    display: block;
    width: 100%;
  }
  @media (max-width: 500px) {
    display: block;
    width: 100%;
  }
}
</style>

<style lang="scss">
  .not-valid{
    .empty, .error{
      border: 1px solid red;
    }
  }
</style>
